import type { CoreUser, CoreUserAttributes } from "~/types/auth"
import { userSessionState } from "@aschehoug/aschehoug-oidc-client"
import { useQuery } from "@tanstack/vue-query"
import { joinURL, withQuery } from "ufo"

export function useAuth() {
  const { terminateDeviceSession } = useDeviceLimiter()

  const redirect = useRedirectCookie()

  const userState = ref(userSessionState.loggedOut)

  const api = useApiClients()

  const client = useOidcClient()

  const { data: user } = useQuery({
    enabled: computed(() => userState.value === userSessionState.hasActiveSession),
    queryKey: ["user"],
    queryFn: () => api.external<CoreUser>("/rest/frontend/session/user"),
    select(coreUser) {
      return {
        firstName: coreUser.firstName,
        lastName: coreUser.lastName,
        fullName: coreUser.fullName,
        uid: coreUser.userId,
        email: coreUser.username,
        source: coreUser.source,
      }
    },
  })

  async function init() {
    try {
      userState.value = await client.startAuthSession()

      if (userState.value === userSessionState.hasActiveSession) {
        const fromPath = redirect.value

        if (fromPath) {
          redirect.value = null
          await navigateTo(fromPath, { replace: true })
        }

        const userAttributes = await api.external<CoreUserAttributes>("/rest/frontend/session/attributes")
        return userAttributes.gaHash
      }
    } catch (e) {
      captureException(e)

      if (isUnauthorized(e)) {
        await logout()
      }
    }
  }

  async function logout(redirectPath = "") {
    try {
      await terminateDeviceSession(user.value?.uid)
      await client.logout()

      location.href = `${redirectPath ? `/produkter/${redirectPath}` : "/"}`
    } catch (error) {
      captureException(error, {
        tags: {
          action: "logout",
        },
      })
    }
  }

  const { id } = useRuntimeConfig().public

  const origin = useCurrentOrigin()

  const cookies = ["am-frontend-sso", "am-sticky", "amlbcookie"] as const

  const loginUrl = (service: Idsp) => {
    const query: Record<string, string> = {
      returnUrl: withQuery(
        joinURL(id.baseUrl, "/am/XUI/"),
        {
          service: IDSP_MAPPING[service],
          goto: joinURL(origin, "/am"),
          gotoOnFail: `${id.cloud}/feil`,
          realm: "/",
        },
      ),
    }

    const domain = id.baseUrl.replace("https://am.core", "")
    for (const cookie of cookies) {
      query[`cookies[${cookie}]`] = domain
    }

    return withQuery(
      joinURL(id.baseUrl, "/uf-auth-cookie-cleaner/delete-cookies"),
      query,
    )
  }

  async function login(service: Idsp, fromPath?: string) {
    redirect.value = fromPath || "/"
    await navigateTo(loginUrl(service), {
      external: true,
    })
  }

  return {
    user,
    init,
    loginUrl,
    login,
    logout,
    isAuthorized: computed(() => !!user.value?.uid),
    isFeide: computed(() => user.value?.source === "FEIDE"),
  }
}
